import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, MapPin, FileText, X } from "lucide-react";
import type { AppointmentFormData } from "@/hooks/useAppointments";

interface AppointmentFormProps {
  onSubmit: (data: AppointmentFormData) => void;
  onCancel: () => void;
  loading?: boolean;
  initialData?: Partial<AppointmentFormData>;
  title?: string;
}

const AppointmentForm = ({ onSubmit, onCancel, loading, initialData, title = "Proposer un rendez-vous" }: AppointmentFormProps) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split("T")[0];

  const [locationError, setLocationError] = useState(false);

  const [form, setForm] = useState<AppointmentFormData>({
    startsAt: initialData?.startsAt || defaultDate,
    startTime: initialData?.startTime || "09:00",
    durationMinutes: initialData?.durationMinutes || 30,
    locationType: initialData?.locationType || "clientAddress",
    locationText: initialData?.locationText || "",
    notes: initialData?.notes || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.startsAt || !form.startTime) return;
    if (form.locationType !== "call" && !form.locationText.trim()) {
      setLocationError(true);
      return;
    }
    setLocationError(false);
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 border rounded-xl bg-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          {title}
        </h3>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs flex items-center gap-1 mb-1">
            <Calendar className="h-3 w-3" /> Date *
          </Label>
          <Input
            type="date"
            value={form.startsAt}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1 mb-1">
            <Clock className="h-3 w-3" /> Heure *
          </Label>
          <Input
            type="time"
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <Label className="text-xs mb-1 block">Durée *</Label>
        <Select
          value={String(form.durationMinutes)}
          onValueChange={(v) => setForm({ ...form, durationMinutes: Number(v) })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="15">15 minutes</SelectItem>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="45">45 minutes</SelectItem>
            <SelectItem value="60">1 heure</SelectItem>
            <SelectItem value="90">1h30</SelectItem>
            <SelectItem value="120">2 heures</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs flex items-center gap-1 mb-1">
          <MapPin className="h-3 w-3" /> Lieu *
        </Label>
        <Select
          value={form.locationType}
          onValueChange={(v: "clientAddress" | "call" | "other") => setForm({ ...form, locationType: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="clientAddress">Chez le client</SelectItem>
            <SelectItem value="call">Appel / Visio</SelectItem>
            <SelectItem value="other">Autre</SelectItem>
          </SelectContent>
        </Select>
        {form.locationType !== "call" && (
          <>
            <Input
              className={`mt-2 ${locationError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              placeholder={form.locationType === "clientAddress" ? "Adresse du client" : "Précisez le lieu"}
              value={form.locationText}
              onChange={(e) => { setForm({ ...form, locationText: e.target.value }); setLocationError(false); }}
              required
            />
            {locationError && (
              <p className="text-xs text-destructive mt-1">Ce champ est requis</p>
            )}
          </>
        )}
      </div>

      <div>
        <Label className="text-xs flex items-center gap-1 mb-1">
          <FileText className="h-3 w-3" /> Note (optionnel)
        </Label>
        <Textarea
          placeholder="Ex: Je passerai pour voir les travaux et confirmer le devis"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="min-h-[50px] text-sm"
          maxLength={500}
        />
      </div>

      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" variant="hero" className="flex-1" disabled={loading}>
          {loading ? "Envoi..." : "Envoyer la proposition"}
        </Button>
      </div>
    </form>
  );
};

export default AppointmentForm;
